import { RFQ } from "../../../types/rfq";
import { useUser } from "../../../contexts/UserContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Path imports, not the @mui/icons-material barrel: the barrel is ~2,100
// modules, and loading it cold took the email-preview suites' beforeEach past
// its 30s hook timeout on a loaded machine.
import CloseIcon from "@mui/icons-material/Close";
import React, { useState, useEffect, useCallback } from 'react';
import { getRFQOriginalSubject } from "../../../api/client";
import { generateDefaultEmailBody } from "./emailTemplates";
import { customerProfile } from "../../../utils/customerRules";
import { useQuoteAttachments } from "./email-preview/useQuoteAttachments";
import { useQuotePdf } from "./email-preview/useQuotePdf";
import { useQuoteExcel } from "./email-preview/useQuoteExcel";
import { useSendQuote } from "./email-preview/useSendQuote";
import AttachmentsPanel from "./email-preview/AttachmentsPanel";
import ExcelAutomationPanel from "./email-preview/ExcelAutomationPanel";
import CcField from "./email-preview/CcField";
import { EmailDialogActions, PdfPreviewDialog, PortalNotice, ResetConfirmDialog } from "./email-preview/EmailDialogParts";

interface EmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  rfq: RFQ;
}

// Constants for subject formatting - could be moved to a config file later
const SUBJECT_CONSTANTS = {
  REPLY_PREFIX: 'Re: ',
  DEFAULT_SUBJECT_PREFIX: 'Quote for RFQ #',
};

/**
 * The reply's subject: the original with "Re: " added once (kept byte for
 * byte for Seven Seas), or "Quote for RFQ #n" when there is no original.
 */
function formatReplySubject(originalSubject: string | null, keepSubject: boolean, rfqId: number): string {
  if (originalSubject) {
    const trimmedSubject = originalSubject.trim();
    // Seven Seas requires the subject byte-for-byte unchanged
    if (keepSubject) {
      return trimmedSubject;
    }
    // Already a reply (case insensitive): don't add the prefix again
    if (trimmedSubject.toLowerCase().startsWith(SUBJECT_CONSTANTS.REPLY_PREFIX.toLowerCase())) {
      return trimmedSubject;
    }
    return `${SUBJECT_CONSTANTS.REPLY_PREFIX}${trimmedSubject}`;
  }
  return `${SUBJECT_CONSTANTS.DEFAULT_SUBJECT_PREFIX}${rfqId}`;
}

const EmailPreviewDialog: React.FC<EmailPreviewDialogProps> = ({ open, onClose, rfq }) => {
  const { selectedUser, customSignoff } = useUser();

  // A user in session storage but not in the context means the two are out of sync
  useEffect(() => {
    const sessionUser = window.sessionStorage.getItem('selectedSignoffUser');
    if (sessionUser && !selectedUser) {
      console.error("Session storage has user but context doesn't!", {
        sessionUser,
        contextUser: selectedUser
      });
    }
  }, []);

  const [originalSubject, setOriginalSubject] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  const [savedEmailContent, setSavedEmailContent] = useState('');
  const [subject, setSubject] = useState<string>('');
  const [savedSubject, setSavedSubject] = useState('');
  // Subject and body are frozen separately. One shared flag meant typing in
  // the body before the original subject loaded froze the subject at
  // "Quote for RFQ #N" - breaking Seven Seas' byte-identical subject rule.
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [bodyEdited, setBodyEdited] = useState(false);
  // The generated body the user's edits started from. When the generated body
  // later differs (prices or items changed), their text may quote stale
  // prices: it is flagged, never overwritten.
  const bodyBaseRef = React.useRef<string | null>(null);
  const [bodyOutdated, setBodyOutdated] = useState(false);
  const hasUserEdits = subjectEdited || bodyEdited;
  const [ccEmails, setCcEmails] = useState<string>('dbrsystems@dbr-sys.com');
  const [showExcelSection, setShowExcelSection] = useState(false);

  // Per-customer envelope rules (Seven Seas, wrist.com, the Archangel and
  // epbrowser portals) - see utils/customerRules.ts. Seven Seas is enforced
  // again at send by backend services/customers/sevenSeas.js.
  const { client_email, original_subject, excel_contact_email } = rfq;
  const profile = React.useMemo(
    () => customerProfile({ client_email, original_subject, excel_contact_email }, originalSubject, subject),
    [client_email, original_subject, excel_contact_email, originalSubject, subject],
  );

  // Seven Seas: no CC at all - not even the usual dbrsystems copy-chip default.
  useEffect(() => {
    if (open && profile.ccLocked) {
      setCcEmails((prev) => (prev ? '' : prev));
    }
  }, [open, profile.ccLocked]);

  // Prefill CC with the person behind an automated mailbox (wrist.com's Excel
  // contact, or the Archangel portal's sender email) - see customerProfile.
  useEffect(() => {
    if (profile.ccLocked) return;
    const prefill = profile.ccPrefill;
    if (open && prefill) {
      setCcEmails((prev) => {
        const trimmed = (prev || '').trim();
        if (!trimmed) return prefill;
        // Avoid duplicates
        const existing = trimmed.split(',').map(e => e.trim().toLowerCase());
        if (existing.includes(prefill.toLowerCase())) return prev;
        return `${trimmed}, ${prefill}`;
      });
    }
  }, [open, rfq.client_email, profile.ccPrefill]);

  // Check if all items are price validated
  // Do not mutate rfq.items; check validation on a non-mutating copy
  const allItemsValidated = (rfq.items || [])
    .slice()
    .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id))
    .every(item => item.price_validated);

  const attachments = useQuoteAttachments(rfq, open);
  const pdf = useQuotePdf(rfq, open, allItemsValidated);
  // Seven Seas quotes must land in their excel file too - same auto-run as wrist.com.
  const excel = useQuoteExcel(rfq, {
    open,
    allItemsValidated,
    autoRun: profile.autoExcel,
    autoRunFor: profile.portal === 'seven_seas' ? 'Seven Seas' : 'Wrist',
    sevenSeasPortal: profile.portal === 'seven_seas',
    setFileAttachments: attachments.setFileAttachments,
  });

  // Format the subject line for replies
  const getFormattedSubject = React.useCallback(
    () => formatReplySubject(originalSubject, profile.keepSubject, rfq.rfq_id),
    [originalSubject, rfq.rfq_id, profile.keepSubject],
  );

  // Refresh email content when dialog opens
  useEffect(() => {
    if (open) {
      // Fetch original subject if we don't have it
      if (!originalSubject) {
        getOriginalSubject();
      }

      // Initialize content only if we don't have any saved content
      if (!savedEmailContent || !savedSubject) {
        const initialSubject = getFormattedSubject();
        setSubject(initialSubject);
        setSavedSubject(initialSubject);

        const initialBody = generateDefaultEmailBody(rfq, selectedUser, customSignoff);
        setEmailContent(initialBody);
        setSavedEmailContent(initialBody);
      } else {
        // Restore saved content if it exists
        setSubject(savedSubject);
        setEmailContent(savedEmailContent);
      }
    }
    // Nothing is reset on close: handleSendEmail calls onClose() before the send
    // resolves, so fileAttachments has to stay intact after the dialog is gone.
  }, [open, rfq, selectedUser, originalSubject, getFormattedSubject, savedEmailContent, savedSubject]);

  // Keep subject in sync with original subject changes
  useEffect(() => {
    // Only a subject the user typed is kept; a body edit does not freeze it
    if (originalSubject && !subjectEdited) {
      const formattedSubject = getFormattedSubject();
      setSubject(formattedSubject);
    }
  }, [originalSubject, getFormattedSubject, subjectEdited]);

  // Fetch original subject and UID for threading
  const getOriginalSubject = useCallback(async () => {
    try {
      // Get the original subject from the API
      const result = await getRFQOriginalSubject(rfq.rfq_id);
      setOriginalSubject(result.original_subject);
    } catch (error) {
      console.error('Error fetching original subject:', error);
    }
  }, [rfq.rfq_id]);

  // Effect to handle RFQ item changes
  useEffect(() => {
    const updatedBody = generateDefaultEmailBody(rfq, selectedUser, customSignoff);
    if (!bodyEdited) {
      setEmailContent(updatedBody);
      setSavedEmailContent(updatedBody);
    } else if (bodyBaseRef.current !== null && updatedBody !== bodyBaseRef.current) {
      // The user's text was written against other prices. Their edits are
      // not overwritten; they are told instead.
      setBodyOutdated(true);
    }

    if (!subjectEdited) {
      const updatedSubject = getFormattedSubject();
      setSubject(updatedSubject);
      setSavedSubject(updatedSubject);
    }
  }, [rfq.items, selectedUser, pdf.attachPdf, getFormattedSubject, bodyEdited, subjectEdited]);

  // When content changes, mark that the body has user edits
  const handleEmailContentChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newContent = e.target.value;
    if (!bodyEdited) {
      // Still the generated body: the base the edits start from
      bodyBaseRef.current = emailContent;
    }
    setEmailContent(newContent);
    setSavedEmailContent(newContent);
    setBodyEdited(true);
  };

  // When subject changes, mark that the subject has user edits
  const handleSubjectChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newSubject = e.target.value;
    setSubject(newSubject);
    setSavedSubject(newSubject);
    setSubjectEdited(true);
  };

  const { handleSendEmail, isSubmitting, blockedReason } = useSendQuote({
    rfq,
    profile,
    originalSubject,
    subject,
    emailContent,
    ccEmails,
    fileAttachments: attachments.fileAttachments,
    savedRemarkFiles: attachments.savedRemarkFiles,
    excludedSavedRemarkFileIds: attachments.excludedSavedRemarkFileIds,
    attachPdf: pdf.attachPdf,
    pdfData: pdf.pdfData,
    isPdfLoading: pdf.isLoadingPdf,
    isExcelBusy: excel.isBusy,
  }, onClose);

  // Function to reset email content to default
  const handleResetEmailContent = () => {
    const initialSubject = getFormattedSubject();
    const initialBody = generateDefaultEmailBody(rfq, selectedUser, customSignoff);
    setSubject(initialSubject);
    setSavedSubject(initialSubject);
    setEmailContent(initialBody);
    setSavedEmailContent(initialBody);
    setSubjectEdited(false);
    setBodyEdited(false);
    bodyBaseRef.current = null;
    setBodyOutdated(false);
    setResetConfirmOpen(false);
  };

  // Layout only: full screen below 900px; the body grows with the window height
  // so the actions stay in view on a 768px laptop and the space is used on
  // tall monitors.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isShortWindow = useMediaQuery('(max-height: 820px)');
  const isTallWindow = useMediaQuery('(min-height: 1050px)');
  const bodyRows = isTallWindow ? 20 : isShortWindow ? 11 : 14;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{ sx: fullScreen ? undefined : { maxWidth: { lg: 1200, xl: 1400 } } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, py: 1.5 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 }}>
              Email Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {`RFQ #${rfq.rfq_id}${rfq.client_name ? ` · ${rfq.client_name}` : ''}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close email preview" sx={{ mt: 0.25 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers onPaste={attachments.handlePaste} sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <PortalNotice portal={profile.portal} />
          {/* Wide screens: compose on the left, attachments and Excel on the
              right. Narrower: one column, same order. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px', xl: 'minmax(0, 1fr) 420px' },
              columnGap: 3,
              rowGap: 2,
              alignItems: 'start',
            }}
          >
          <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 2,
              alignItems: 'start',
            }}
          >
          {/* To field (read-only) */}
          <TextField
            fullWidth
            label="To"
            value={rfq.client_email}
            margin="dense"
            disabled
            InputProps={{
              readOnly: true,
            }}
          />

          <CcField value={ccEmails} onChange={setCcEmails} disabled={isSubmitting} locked={profile.ccLocked} />
          </Box>

          {/* Subject field */}
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={handleSubjectChange}
            margin="dense"
            disabled={isSubmitting}
          />

          {bodyOutdated && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              The quote's items, prices or remarks changed after you edited the
              email body, so the body may be out of date. Check it before sending,
              or use Reset to Default to regenerate it (your edits will be lost).
            </Alert>
          )}

          {/* Email body field */}
          <TextField
            fullWidth
            multiline
            rows={bodyRows}
            label="Email Content"
            value={emailContent}
            onChange={handleEmailContentChange}
            margin="dense"
            disabled={isSubmitting}
          />
          </Box>

          {/* Attachments and Excel column */}
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, pt: { lg: 1 } }}>
          <AttachmentsPanel
            attachPdf={pdf.attachPdf}
            onAttachPdfChange={pdf.setAttachPdf}
            attachPdfDisabled={isSubmitting || !allItemsValidated}
            savedRemarkFiles={attachments.savedRemarkFiles}
            excludedSavedRemarkFileIds={attachments.excludedSavedRemarkFileIds}
            onToggleSavedRemarkFile={attachments.toggleSavedRemarkFile}
            remarkFilesExcluded={profile.excludeRemarkFiles}
            fileAttachments={attachments.fileAttachments}
            onRemoveAttachment={attachments.removeAttachment}
            onAddFiles={attachments.addFiles}
          />

          <ExcelAutomationPanel
            excel={excel}
            allItemsValidated={allItemsValidated}
            expanded={showExcelSection}
            onToggleExpanded={() => setShowExcelSection(!showExcelSection)}
          />
          </Box>
          </Box>
        </DialogContent>
        <EmailDialogActions
          allItemsValidated={allItemsValidated}
          isPdfLoading={pdf.isLoadingPdf}
          onPreviewPdf={() => pdf.setPdfPreviewOpen(true)}
          hasUserEdits={hasUserEdits}
          onReset={() => setResetConfirmOpen(true)}
          blockedReason={blockedReason}
          isSubmitting={isSubmitting}
          onCancel={onClose}
          onSend={handleSendEmail}
        />
      </Dialog>

      <PdfPreviewDialog
        open={pdf.pdfPreviewOpen}
        onClose={() => pdf.setPdfPreviewOpen(false)}
        pdfData={pdf.pdfData}
        fullScreen={fullScreen}
      />

      <ResetConfirmDialog
        open={resetConfirmOpen}
        onCancel={() => setResetConfirmOpen(false)}
        onConfirm={handleResetEmailContent}
      />
    </>
  );
};

export default EmailPreviewDialog;
